import React, { useCallback, useEffect, useRef, useState } from 'react';
import useUomConvert from 'hooks/useUomConvert';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { useFormikContext } from 'formik';

import PlusIcon from 'assets/plus.svg?react';
import NoCropZonesSelectedIcon from 'assets/properties-map-icon.svg';
import EmptyTableState from 'components/EmptyTableState';
import CropZonesTable from 'components/CropZonesTable';
import TotalAcreageSummary from 'components/CropZonesTable/TotalAcreageSummary';
import { getMappedData } from 'components/CropZonesTable/helpers';
import { FormValues } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import SelectPropertiesDrawerV2 from 'components/SelectPropertiesDrawerV2';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import LayoutConstants from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutConstants';
import useOperationSelectionAdapter from './hooks/useOperationSelectionAdapter';
import { calculateTotalAppliedAcreage } from './helpers/cropZoneMath';
import { getTotalAcreageUpdates } from './helpers/getTotalAcreageUpdates';

const AddCropZonesButton: React.FC<{
  ghost?: boolean;
  onClick: () => void;
}> = ({ ghost = false, onClick }) => (
  <Button
    data-testid={ghost ? undefined : 'select-crop-zones-button'}
    style={{ background: ghost ? 'white' : undefined }}
    type={ButtonType.primary}
    icon={<PlusIcon fill={ghost ? '#14803C' : 'white'} />}
    onClick={onClick}
    ghost={ghost}
  >
    {LayoutStrings.cropZones.selectCropZones}
  </Button>
);

const PropertiesStep: React.FC = () => {
  const {
    values,
    setFieldTouched,
    validateField,
    setFieldValue
  } = useFormikContext<FormValues>();
  const convertFn = useUomConvert();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    utilityState: { selectedCropSeasons },
    updateUtilityState
  } = useAgOperationUtility();

  // Keep a ref to the latest Formik values so the callback passed to
  // CropZonesTable always reads fresh state. CropZonesTable's row handlers
  // intentionally omit the callback from their closures to avoid forcing
  // re-renders on every keystroke, which means the closure they capture
  // would go stale after Formik re-renders. The ref sidesteps that problem.
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Snapshot of zone IDs at the moment the drawer opens. Used by
  // handleCloseDrawer to detect whether the user actually added or removed
  // zones — if the set is unchanged (e.g. opened and cancelled), no total
  // recalculation is needed.
  const drawerInitialZoneIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (isDrawerOpen) {
      drawerInitialZoneIdsRef.current = (valuesRef.current.cropZones ?? []).map(
        z => z.id
      );
    }
  }, [isDrawerOpen]);

  const handleTotalAcreageChange = useCallback(
    (total: number) => {
      Object.entries(
        getTotalAcreageUpdates(valuesRef.current, total, convertFn)
      ).forEach(([field, value]) => setFieldValue(field, value));
    },
    [setFieldValue, convertFn]
  );

  const handleDataChange = (newData: any[]) => {
    // Update the cropZones array
    setFieldValue('cropZones', newData);
  };

  const handleSelectCropZones = () => {
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);

    // Defer reading values until after React processes the drawer's
    // setFieldValue('cropZones', ...) — handleCloseDrawer is called
    // synchronously right after, so valuesRef.current would otherwise still
    // hold the pre-drawer state and we'd compute a stale (often 0) total.
    requestAnimationFrame(() => {
      const currentZones = valuesRef.current.cropZones ?? [];
      const initialIds = drawerInitialZoneIdsRef.current ?? [];
      const currentIds = currentZones.map(z => z.id);

      // Only recompute the total if the user actually added or removed zones.
      // Comparing zone IDs (not totals) is precise — comparing computed sums
      // would re-introduce the floating-point drift the broader fix addresses.
      const zonesChanged =
        currentIds.length !== initialIds.length ||
        currentIds.some(id => !initialIds.includes(id));

      if (zonesChanged) {
        handleTotalAcreageChange(calculateTotalAppliedAcreage(currentZones));
      }
      drawerInitialZoneIdsRef.current = null;

      if (currentZones.length === 0) {
        (document.querySelector(
          '[data-testid="select-crop-zones-button"]'
        ) as HTMLElement | null)?.focus();
      } else {
        (document.querySelector(
          '[data-testid="next-step-button"]'
        ) as HTMLElement | null)?.focus();
      }
    });
  };

  const isEmptyState = !values.cropZones || values.cropZones.length === 0;

  useEffect(() => {
    // Validate cropZones when it changes
    setFieldTouched('cropZones', true);
    validateField('cropZones');
    if (
      values.cropZones?.length &&
      values.cropZones.some(
        zone => !zone.appliedAcreage && !zone.coveragePercent
      )
    ) {
      setFieldValue('cropZones', getMappedData(values.cropZones));
    }
  }, [values.cropZones]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newSelectedCropSeasons = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );
    updateUtilityState({ selectedCropSeasons: newSelectedCropSeasons });
    if (isEmptyState) {
      setIsDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyState]);

  const selectionAdapter = useOperationSelectionAdapter();

  return (
    <>
      {isEmptyState ? (
        <div className="bg-white shadow-sm-card rounded-lg w-full">
          <EmptyTableState
            title={LayoutStrings.cropZones.emptyStateTitle}
            message={LayoutStrings.cropZones.emptyStateMessage}
            overlayIcon={NoCropZonesSelectedIcon}
            button={<AddCropZonesButton onClick={handleSelectCropZones} />}
          />
        </div>
      ) : (
        <TotalAcreageSummary
          totalAppliedAcreage={values.totalAppliedAcreage || 0}
          cropZones={values.cropZones}
          setFieldValue={setFieldValue}
          onTotalAcreageChange={handleTotalAcreageChange}
          addButton={
            <AddCropZonesButton ghost onClick={handleSelectCropZones} />
          }
        >
          <CropZonesTable
            data={values.cropZones}
            onTotalAcreageChange={handleTotalAcreageChange}
            onDataChange={handleDataChange}
            scroll={{ y: LayoutConstants.TABLE_VERTICAL_CROP_ZONES_SCROLL }}
          />
        </TotalAcreageSummary>
      )}
      <SelectPropertiesDrawerV2
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        selectionAdapter={selectionAdapter}
        seasonIds={selectedCropSeasons}
      />
    </>
  );
};

export default PropertiesStep;
